/**
 * buildTimeAdvisor
 * -----------------
 * LAP-765 — Adapter qui transforme les VRAIES données utilisateur en un objet
 * exploitable par la page "Conseiller Temps" (`ConseillerTempsPage`).
 *
 * Pattern identique à `buildAdvisorFromReports` (Conseiller Patrimonial) :
 *   - on agrège les sources Life (profil, roadmap, projets, tâches, événements
 *     calendrier, sports, objectifs, roue de vie/profil temps) ;
 *   - on calcule un score global + une décomposition par dimension ;
 *   - on produit un diagnostic (forces / faiblesses / risques) ;
 *   - on liste des actions prioritaires ;
 *   - on suggère des time-blocks à différents horizons (semaine / mois /
 *     trimestre / année).
 *
 * Toutes les chaînes affichées passent par `t` (i18next) pour rester
 * compatibles avec les 8 langues Life — comme le Wealth Advisor.
 *
 * Tant que le backend ne renvoie pas d'analyse LLM dédiée, ce builder est la
 * source de vérité de l'UI. Quand un endpoint `/api/general/time-advisor/`
 * existera, il pourra remplacer ce calcul (ou l'enrichir).
 */

// LAP-765 — Identity fallback when no t function is provided.
const identityT = (key, opts = {}) => {
  if (opts && typeof opts.defaultValue === 'string') return opts.defaultValue
  return key
}

// LAP-765 — Horizons supportés par les recommandations. L'ordre est important
// (utilisé pour l'affichage onglets / tabs).
export const TIME_HORIZONS = ['week', 'month', 'quarter', 'year']

// LAP-765-v3 — Référentiel des catégories temps, partagé par les blocs
// suggérés (buildWeekBlocks…) et le formulaire d'ajout rapide. Chaque clé
// passe par i18next, avec un repli français via `defaultValue`.
export const TIME_CATEGORIES = [
  { key: 'focus', defaultValue: 'Concentration' },
  { key: 'organisation', defaultValue: 'Organisation' },
  { key: 'sport', defaultValue: 'Sport' },
  { key: 'leisure', defaultValue: 'Loisirs' },
  { key: 'wellbeing', defaultValue: 'Bien-être' },
  { key: 'pilotage', defaultValue: 'Pilotage' },
  { key: 'projects', defaultValue: 'Projets' },
  { key: 'relations', defaultValue: 'Relations' },
  { key: 'rest', defaultValue: 'Repos' },
  { key: 'learning', defaultValue: 'Apprentissage' },
  { key: 'other', defaultValue: 'Autre' },
]

// LAP-765-v3 — Renvoie les libellés de catégories traduits pour alimenter le
// menu déroulant du formulaire d'ajout rapide.
export function getTimeCategoryOptions(t = identityT) {
  return TIME_CATEGORIES.map((c) =>
    t(`conseillerTemps.categories.${c.key}`, { defaultValue: c.defaultValue }),
  )
}

// LAP-765-v2 — Mapping jour-de-semaine ISO (lundi=0..dimanche=6) → flag
// booléen sur le modèle Event (saturday/sunday/monday/...).
const DOW_FLAG_BY_INDEX = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

// LAP-765-v2 — Heures hebdo "par défaut" déduites du type de contrat quand
// l'utilisateur n'a pas saisi explicitement `hours` sur son expérience.
const CONTRACT_DEFAULT_HOURS = {
  CDI: 35,
  CDD: 35,
  'CDI temps partiels': 24,
  'CDD temps partiels': 24,
  Stage: 35,
  'Contrat en alternance': 35,
  'Service civique': 28,
  Indépendant: 40,
  Freelance: 35,
  'Intermittent du spectacle': 30,
}

// LAP-765-v2 — Palette pour la répartition par catégorie temps (Travail,
// Personnel, Social, …). On boucle si la liste de catégories dépasse 8.
const CATEGORY_COLORS = [
  '#7e9cdb',
  '#f2b84b',
  '#7cc6b2',
  '#a987ff',
  '#5aa1d8',
  '#f87171',
  '#facc15',
  '#4ade80',
]

function num(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function safeArr(value) {
  return Array.isArray(value) ? value : []
}

function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value))
}

// ─────────────────────────────────────────────────────────────────────────
// LAP-765-v2 — Recurrence expansion
//
// Le modèle Event Django expose des flags récurrents (is_periodic +
// is_weekly / is_monthly / is_yearly / is_daily, plus les flags par jour
// de la semaine et frequency_end_date / number_of_occurrences).
//
// Le builder de l'advisor doit "déplier" ces patterns en occurrences réelles
// sur la fenêtre d'analyse (par défaut : 30 jours glissants) pour calculer :
//   - la charge effective hebdomadaire (events × durée),
//   - la répartition du temps par catégorie,
//   - la pertinence des recommandations (un événement hebdo récurrent compte
//     bien 4× dans le mois et pas 1 seule fois).
//
// On gère trois types de récurrences :
//   1. `is_periodic` + flag daily/weekly/monthly/yearly (le format moderne).
//   2. `event_type === 'f'` (Fixed) → événement annuel sur day_num/month_num
//      (anniversaires, fêtes…). Émet 0 ou 1 occurrence dans la fenêtre.
//   3. Pas de récurrence → on émet l'événement tel quel si sa date d'origine
//      tombe dans la fenêtre.
// ─────────────────────────────────────────────────────────────────────────

function parseEventDate(event) {
  if (event?.start instanceof Date) return event.start
  if (event?.start) {
    const d = new Date(event.start)
    if (!Number.isNaN(d.getTime())) return d
  }
  if (event?.start_date) {
    // start_date sur le modèle Django arrive en DD-MM-YYYY ou ISO selon source.
    const raw = String(event.start_date)
    const isoLike = raw.length === 10 && raw[4] === '-'
    if (isoLike) {
      const d = new Date(raw)
      if (!Number.isNaN(d.getTime())) return d
    }
    // Fallback DD-MM-YYYY
    const parts = raw.split(/[-/]/)
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10))
      const d = new Date(yyyy, mm - 1, dd)
      if (!Number.isNaN(d.getTime())) return d
    }
  }
  return null
}

function durationMinutes(event) {
  const start =
    event?.start instanceof Date ? event.start : parseEventDate(event)
  const end =
    event?.end instanceof Date ? event.end : event?.end ? new Date(event.end) : null
  if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
    const diff = (end - start) / 60000
    if (diff > 0 && diff < 24 * 60) return Math.round(diff)
  }
  if (event?.is_all_day || event?.isAllDay) return 8 * 60
  if (event?.start_time && event?.end_time) {
    const [sh, sm] = String(event.start_time).split(':').map(Number)
    const [eh, em] = String(event.end_time).split(':').map(Number)
    if ([sh, sm, eh, em].every((n) => Number.isFinite(n))) {
      const diff = eh * 60 + em - (sh * 60 + sm)
      if (diff > 0) return diff
    }
  }
  return 60 // fallback raisonnable : 1h
}

// LAP-765-v3 — Les blocs programmés par le Conseiller Temps inscrivent leur
// catégorie métier ("Sport", "Concentration"…) dans le champ libre `data`
// (JSON) de l'événement. On la lit en priorité : sans ça, toutes les
// occurrences créées retombaient dans « Sans catégorie » et la répartition
// par activité était fausse.
function categoryFromData(event) {
  const raw = event?.data
  if (!raw) return null
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    const cat = parsed && parsed.category
    if (typeof cat === 'string' && cat.trim()) return cat.trim()
  } catch (_) {
    /* `data` n'est pas un JSON exploitable — on ignore proprement. */
  }
  return null
}

function categoryOf(event, categoryNameById = null) {
  return (
    categoryFromData(event) ||
    event?.category ||
    event?.Category ||
    (typeof event?.category_id === 'object' ? event.category_id?.name : null) ||
    // Les événements BRUTS (/api/time/events/) sérialisent `category_id` en
    // ENTIER (PK). Sans résolution via le référentiel Time.Category
    // (/api/time/get-category-dd), tous les événements créés à la main
    // tombaient en « Sans catégorie » — le Maître du temps n'identifiait
    // donc jamais les séances de sport du calendrier.
    (categoryNameById && event?.category_id != null
      ? categoryNameById[event.category_id] || null
      : null) ||
    'Sans catégorie'
  )
}

// Normalisation pour comparer des libellés de catégorie ("Sport", "sport ",
// accents…) sans dépendre de la casse ni de la langue d'affichage.
function normalizeLabel(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

function isSportCategory(label) {
  const norm = normalizeLabel(label)
  // "sport" est identique dans les 5 langues seedées (cf. Time/management/
  // commands/seed_time_categories.py) — on matche par inclusion pour couvrir
  // « Sport & bien-être », "Sports", etc., en évitant « transport ».
  if (!norm) return false
  if (norm.includes('transport')) return false
  return /(^|[^a-z])sports?([^a-z]|$)/.test(norm) || norm === 'sport'
}

/**
 * LAP-765-v2 — Génère les occurrences d'un événement dans [from, to].
 * Renvoie [{ date, durationMinutes, category }].
 */
function expandOccurrences(event, from, to, categoryNameById = null) {
  if (!event) return []
  const occurrences = []
  const dur = durationMinutes(event)
  const cat = categoryOf(event, categoryNameById)
  const push = (date) => {
    if (date >= from && date <= to) {
      occurrences.push({ date, durationMinutes: dur, category: cat })
    }
  }

  // Cas 2 : "Fixed" annuel sur day_num/month_num — anniversaire, fête, etc.
  if (event.event_type === 'f' && event.day_num && event.month_num) {
    const year = from.getFullYear()
    // On émet pour l'année courante et la suivante au cas où la fenêtre
    // chevauche un 31 décembre.
    ;[year, year + 1].forEach((y) => {
      const d = new Date(y, event.month_num - 1, event.day_num)
      if (!Number.isNaN(d.getTime())) push(d)
    })
    return occurrences
  }

  // Cas 1 : événement périodique (is_periodic).
  if (event.is_periodic) {
    const origin = parseEventDate(event) || from
    // Borne supérieure d'itération : min(frequency_end_date, to).
    const endBoundary = event.frequency_end_date
      ? new Date(
          // frequency_end_date arrive parfois en DD-MM-YYYY
          parseEventDate({ start_date: event.frequency_end_date })?.getTime() || to.getTime(),
        )
      : to
    const stop = new Date(Math.min(endBoundary.getTime(), to.getTime()))

    const step = Math.max(1, parseInt(event.repeat_after, 10) || 1)
    const cursor = new Date(origin.getTime())
    let count = 0
    const max = event.number_of_occurrences
      ? parseInt(event.number_of_occurrences, 10)
      : 200

    if (event.is_daily) {
      while (cursor <= stop && count < max) {
        if (cursor >= from) push(new Date(cursor))
        cursor.setDate(cursor.getDate() + step)
        count += 1
      }
    } else if (event.is_weekly) {
      // Récolte les jours actifs (lundi..dimanche)
      const activeDow = DOW_FLAG_BY_INDEX.filter((flag) => !!event[flag])
      // Si aucun jour explicite, on prend le DOW de l'origine.
      const fallbackFlag = DOW_FLAG_BY_INDEX[(origin.getDay() + 6) % 7]
      const days = activeDow.length ? activeDow : [fallbackFlag]
      // On itère semaine par semaine sur la fenêtre.
      const weekCursor = new Date(cursor.getTime())
      while (weekCursor <= stop && count < max) {
        days.forEach((flag) => {
          const idx = DOW_FLAG_BY_INDEX.indexOf(flag) // 0=monday … 6=sunday
          // Convert ISO index (Mon=0) → JS Date.getDay (Sun=0)
          const jsDow = idx === 6 ? 0 : idx + 1
          const date = new Date(weekCursor.getTime())
          const delta = (jsDow - date.getDay() + 7) % 7
          date.setDate(date.getDate() + delta)
          if (date >= from && date <= stop) {
            push(date)
            count += 1
          }
        })
        weekCursor.setDate(weekCursor.getDate() + 7 * step)
      }
    } else if (event.is_monthly) {
      while (cursor <= stop && count < max) {
        if (cursor >= from) push(new Date(cursor))
        cursor.setMonth(cursor.getMonth() + step)
        count += 1
      }
    } else if (event.is_yearly) {
      while (cursor <= stop && count < max) {
        if (cursor >= from) push(new Date(cursor))
        cursor.setFullYear(cursor.getFullYear() + step)
        count += 1
      }
    } else {
      // is_periodic mais pas de granularité reconnue → on garde l'origine.
      push(origin)
    }
    return occurrences
  }

  // Cas 3 : événement ponctuel — on émet 1 occurrence à sa date d'origine.
  const origin = parseEventDate(event)
  if (origin) push(origin)
  return occurrences
}

/**
 * LAP-765-v2 — Calcule la répartition du temps occupé par catégorie sur la
 * fenêtre `[from, to]`. Renvoie un tableau trié par minutes décroissantes.
 */
function computeTimeBreakdown(allOccurrences) {
  const byCat = {}
  let total = 0
  allOccurrences.forEach((o) => {
    const key = o.category || 'Sans catégorie'
    byCat[key] = (byCat[key] || 0) + (o.durationMinutes || 0)
    total += o.durationMinutes || 0
  })
  const entries = Object.entries(byCat)
    .map(([category, minutes], i) => ({
      category,
      minutes,
      hours: Math.round(minutes / 6) / 10, // arrondi 0.1h
      percent: total > 0 ? Math.round((minutes / total) * 100) : 0,
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    }))
    .sort((a, b) => b.minutes - a.minutes)
  return { entries, totalMinutes: total }
}

/**
 * LAP-765-v2 — Récupère l'emploi courant depuis MonParcoursPro. Cherche en
 * priorité un poste marqué `CurrentPoste: true` ; à défaut un poste sans
 * `EndDate`. Renvoie `{ jobTitle, employer, contractType, weeklyWorkHours }`.
 * `weeklyWorkHours` est déduit du champ `hours` (s'il est ≤ 60) ou du type
 * de contrat (CDI=35, temps partiel=24, etc.).
 */
function extractEmployment(experiences) {
  const arr = safeArr(experiences)
  if (!arr.length) return null
  const current =
    arr.find((e) => e?.CurrentPoste === true) ||
    arr.find((e) => !e?.EndDate || e?.EndDate === '' || e?.EndDate === null)
  if (!current) return null
  const rawHours = num(current.hours, null)
  let weeklyWorkHours = null
  if (rawHours != null && rawHours > 0 && rawHours <= 60) {
    weeklyWorkHours = rawHours
  } else if (current.ContractType && CONTRACT_DEFAULT_HOURS[current.ContractType]) {
    weeklyWorkHours = CONTRACT_DEFAULT_HOURS[current.ContractType]
  }
  return {
    jobTitle: current.Name || current.PosteOccupe || '',
    employer: current.Entreprise || '',
    contractType: current.ContractType || '',
    weeklyWorkHours,
  }
}

/**
 * Heuristique de scoring (0-100) calquée sur scoringEngine du Wealth Advisor.
 * Chaque dimension renvoie un score 0-100 + un statut critical/warning/good.
 */
function statusFromScore(score) {
  if (score >= 70) return 'good'
  if (score >= 45) return 'warning'
  return 'critical'
}

function scoreCalendarLoad({ eventsPerWeek, weeklyCapacity = 40 }) {
  // Trop d'événements = surcharge ; trop peu = sous-planification.
  if (eventsPerWeek == null) return 50
  const ratio = eventsPerWeek / weeklyCapacity
  if (ratio >= 0.6 && ratio <= 0.85) return 90
  if (ratio > 0.85 && ratio <= 1.1) return 65
  if (ratio > 1.1) return clamp(100 - (ratio - 1.1) * 80, 10, 50)
  // Sous-planifié
  return clamp(40 + ratio * 60, 25, 80)
}

function scoreTaskBacklog({ pendingTasks, overdueTasks }) {
  if (pendingTasks == null) return 50
  const penalty = (overdueTasks || 0) * 6
  if (pendingTasks <= 10) return clamp(95 - penalty, 30, 100)
  if (pendingTasks <= 25) return clamp(75 - penalty, 25, 90)
  if (pendingTasks <= 50) return clamp(55 - penalty, 15, 70)
  return clamp(35 - penalty, 5, 55)
}

function scoreObjectivesAlignment({ activeObjectifs, projetsActifs }) {
  // Avoir des objectifs ET des projets actifs = excellent alignement.
  if (!activeObjectifs && !projetsActifs) return 25
  if (activeObjectifs && !projetsActifs) return 55
  if (!activeObjectifs && projetsActifs) return 60
  // Both present, but cap based on raw counts.
  return clamp(70 + Math.min(activeObjectifs, 5) * 4 + Math.min(projetsActifs, 5) * 2, 60, 100)
}

function scoreLifeBalance({ wheelOfLife }) {
  // wheelOfLife : map { domain: 0..10 }
  const values = Object.values(wheelOfLife || {}).map((v) => num(v, 0))
  if (!values.length) return 50
  const avg = values.reduce((a, b) => a + b, 0) / values.length
  const min = Math.min(...values)
  // Pénalité si une dimension est très basse (déséquilibre).
  const penalty = Math.max(0, 5 - min) * 6
  return clamp(avg * 10 - penalty, 5, 100)
}

function scoreSportConsistency({ sportSessionsLast30d, sportObjectivePerWeek }) {
  if (sportObjectivePerWeek == null && sportSessionsLast30d == null) return 50
  const target = (sportObjectivePerWeek || 2) * 4 // ~30 jours
  const actual = sportSessionsLast30d || 0
  if (!target) return 50
  const ratio = actual / target
  return clamp(ratio * 100, 0, 100)
}

function scoreRoadmapPlanning({ roadmapIdeasCount, plannedThisQuarter }) {
  if (!roadmapIdeasCount) return 35
  if (!plannedThisQuarter) return 50
  return clamp(60 + Math.min(plannedThisQuarter, 4) * 8, 60, 96)
}

// ─────────────────────────────────────────────────────────────────────────
// Public entry point
// ─────────────────────────────────────────────────────────────────────────

export function buildTimeAdvisor({
  user = {},
  events = [],
  tasks = [],
  projects = [],
  objectifs = [],
  roadmapIdeas = [],
  sportSessions = [],
  // Référentiel Time.Category ([{ id, name }]) : indispensable pour résoudre
  // le `category_id` numérique des événements bruts (cf. categoryOf).
  categories = [],
  wheelOfLife = {},
  timeProfile = {},
  // LAP-765-v2 — Emploi courant (depuis MonParcoursPro). Soit on reçoit la
  // liste brute des expériences, soit déjà un objet pré-extrait.
  experiences = [],
  employment: employmentArg = null,
  t = identityT,
} = {}) {
  // ── Profil utilisateur ────────────────────────────────────────────────
  const firstName = user.first_name || user.firstName || user.prenom || ''
  const interests = safeArr(user.centres_dinteret || user.interests)
  const concerns = safeArr(user.preoccupations || user.concerns)

  // ── Emploi (LAP-765-v2) ──────────────────────────────────────────────
  const employment = employmentArg || extractEmployment(experiences)

  // ── Calendrier + récurrences (LAP-765-v2) ────────────────────────────
  // On déplie *toutes* les récurrences (échéanciers) sur deux fenêtres :
  // 7 jours pour la charge hebdo, 30 jours pour la répartition catégorie.
  // Sans ça les événements répétés ne comptaient qu'une seule fois et la
  // synthèse sous-estimait gravement le temps réellement occupé.
  const now = new Date()
  const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 3600 * 1000)
  const oneMonthFromNow = new Date(now.getTime() + 30 * 24 * 3600 * 1000)
  const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 3600 * 1000)

  // Map id → nom du référentiel Time.Category, pour résoudre les
  // `category_id` numériques des événements bruts.
  const categoryNameById = {}
  safeArr(categories).forEach((c) => {
    if (c && c.id != null && c.name) categoryNameById[c.id] = c.name
  })

  const eventsArr = safeArr(events)
  const weekOccurrences = eventsArr.flatMap((e) =>
    expandOccurrences(e, now, oneWeekFromNow, categoryNameById),
  )
  const monthOccurrences = eventsArr.flatMap((e) =>
    expandOccurrences(e, now, oneMonthFromNow, categoryNameById),
  )
  const eventsThisWeek = weekOccurrences
  const eventsThisMonth = monthOccurrences
  const weeklyEventMinutes = weekOccurrences.reduce(
    (acc, o) => acc + (o.durationMinutes || 0),
    0,
  )

  // ── Procrastination / événements sans cesse reportés (LAP-770) ───────────
  // Le backend incrémente Event.reschedule_count à chaque modification de
  // date/heure (cf. Time/serializers.py). On repère ici les événements
  // « collants » : ceux dont la planification a été déplacée ≥ 2 fois. Ils
  // signalent souvent une tâche qu'on repousse sans la traiter.
  const RESCHEDULE_THRESHOLD = 2
  const procrastinatedEvents = eventsArr
    .filter((e) => num(e.reschedule_count, 0) >= RESCHEDULE_THRESHOLD)
    .map((e) => ({
      id: e.id,
      titre: e.titre || e.title || e.name || '',
      rescheduleCount: num(e.reschedule_count, 0),
    }))
    .sort((a, b) => b.rescheduleCount - a.rescheduleCount)
  const procrastinatedCount = procrastinatedEvents.length

  // ── Tâches ────────────────────────────────────────────────────────────
  const tasksArr = safeArr(tasks)
  const pendingTasks = tasksArr.filter((tk) => !tk.faite && !tk.completed).length
  const overdueTasks = tasksArr.filter((tk) => {
    if (tk.faite || tk.completed) return false
    const due = tk.due_date || tk.DateFinObjectif || tk.deadline
    if (!due) return false
    const d = new Date(due)
    return !Number.isNaN(d.getTime()) && d < now
  }).length

  // ── Projets actifs ───────────────────────────────────────────────────
  const projectsArr = safeArr(projects)
  const projetsActifs = projectsArr.filter((p) => !p.archived && !p.archive).length

  // ── Objectifs ────────────────────────────────────────────────────────
  const objectifsArr = safeArr(objectifs)
  const activeObjectifs = objectifsArr.filter((o) => {
    const status = (o.status || o.statut || '').toLowerCase()
    return status !== 'annule' && status !== 'cancelled' && status !== 'fait'
  }).length

  // ── Roadmap ──────────────────────────────────────────────────────────
  const roadmapArr = safeArr(roadmapIdeas)
  const plannedThisQuarter = roadmapArr.filter((r) => {
    const horizon = (r.horizon || '').toLowerCase()
    return horizon === 'quarter' || horizon === 'q' || r.planned_quarter
  }).length

  // ── Sport ────────────────────────────────────────────────────────────
  // Séances déclarées côté Health (endpoint optionnel — souvent absent).
  const healthSportSessionsLast30d = safeArr(sportSessions).filter((s) => {
    const d = new Date(s.date || s.created_at)
    if (Number.isNaN(d.getTime())) return false
    return now - d <= 30 * 24 * 3600 * 1000
  }).length
  // Séances SPORT du calendrier sur les 30 DERNIERS jours : on déplie les
  // occurrences sur la fenêtre passée (récurrences comprises) et on garde
  // celles dont la catégorie résolue est « Sport ». Sans ça, le Maître du
  // temps ignorait totalement les événements sport du calendrier et la
  // « Régularité sport » restait à zéro / non évaluée.
  const calendarSportOccurrencesLast30d = eventsArr
    .flatMap((e) => expandOccurrences(e, oneMonthAgo, now, categoryNameById))
    .filter((o) => isSportCategory(o.category))
  const calendarSportSessionsLast30d = calendarSportOccurrencesLast30d.length
  const sportSessionsLast30d =
    healthSportSessionsLast30d + calendarSportSessionsLast30d
  const sportObjectivePerWeek = num(user.sport_objective_per_week, null)

  // ── Profil temps (depuis General/views.py — wheel of life / time profile)
  //
  // LAP-765-v2 — La capacité hebdo de "temps disponible hors emploi" est
  // calculée à partir du time profile (si défini) MOINS les heures de travail
  // déclarées sur le poste courant. Sans emploi déclaré on garde la capacité
  // brute de 40h. Sans time profile mais avec emploi, on suppose 168h - sommeil
  // (56h) - travail = budget hebdo "actif".
  const rawWeeklyCapacity = num(timeProfile.weekly_capacity_hours, null)
  const workHours = employment?.weeklyWorkHours ?? 0
  let weeklyCapacity
  if (rawWeeklyCapacity != null) {
    weeklyCapacity = rawWeeklyCapacity
  } else if (workHours > 0) {
    // 168h/sem - 56h sommeil - workHours = budget personnel restant.
    weeklyCapacity = Math.max(20, 168 - 56 - workHours)
  } else {
    weeklyCapacity = 40
  }
  const weeklyEventHours = Math.round((weeklyEventMinutes / 60) * 10) / 10
  // Pour le scoring, on compare les heures occupées par les événements
  // (récurrences dépliées) à la capacité — c'est plus fidèle que le simple
  // décompte d'événements. On garde aussi `eventsPerWeek` (raw count) pour
  // les recommandations.
  const eventsPerWeek = weeklyEventHours || eventsThisWeek.length

  // ── Dimensions du score global ───────────────────────────────────────
  //
  // LAP-765-v4 — Chaque dimension porte désormais un flag `available` : il
  // distingue « aucune donnée » de « tout va bien ». Sans ce flag, un
  // utilisateur sans tâche obtenait 95/100 en « Backlog tâches » (0 tâche
  // tombait dans la branche « ≤ 10 tâches » → score excellent) et ressortait
  // donc « au top » alors qu'il n'avait simplement rien renseigné.
  //
  // Quand `available` est faux :
  //   - la dimension n'entre PAS dans le score global (poids re-normalisé) ;
  //   - elle n'est classée ni en force, ni en faiblesse, ni en risque ;
  //   - l'UI l'affiche « Non évalué » au lieu d'un chiffre trompeur.
  //
  // `dataSource` documente les données réellement utilisées (affiché dans la
  // page), `emptyHint` explique quoi renseigner pour activer la dimension.
  const wheelValues = Object.values(wheelOfLife || {})
    .map((v) => num(v, null))
    .filter((v) => v != null)

  const dimensions = [
    {
      key: 'load',
      label: t('conseillerTemps.dimensions.load', { defaultValue: 'Charge calendrier' }),
      score: scoreCalendarLoad({ eventsPerWeek, weeklyCapacity }),
      weight: 0.2,
      available: eventsArr.length > 0,
      detail: t('conseillerTemps.dimensions.loadDetail', {
        defaultValue: 'Rapport entre événements à venir et capacité hebdomadaire.',
      }),
      dataSource: t('conseillerTemps.dimensions.loadData', {
        defaultValue:
          'Données : événements de votre calendrier (récurrences dépliées) comparés à votre capacité hebdomadaire.',
      }),
      emptyHint: t('conseillerTemps.dimensions.loadEmpty', {
        defaultValue: 'Aucun événement dans votre calendrier — ajoutez-en pour activer ce score.',
      }),
    },
    {
      key: 'backlog',
      label: t('conseillerTemps.dimensions.backlog', { defaultValue: 'Backlog tâches' }),
      score: scoreTaskBacklog({ pendingTasks, overdueTasks }),
      weight: 0.2,
      available: tasksArr.length > 0,
      detail: t('conseillerTemps.dimensions.backlogDetail', {
        defaultValue: 'Volume et retard des tâches non terminées.',
      }),
      dataSource: t('conseillerTemps.dimensions.backlogData', {
        defaultValue: 'Données : vos tâches non terminées et celles en retard.',
      }),
      emptyHint: t('conseillerTemps.dimensions.backlogEmpty', {
        defaultValue: 'Aucune tâche enregistrée — ajoutez vos tâches pour activer ce score.',
      }),
    },
    {
      key: 'alignment',
      label: t('conseillerTemps.dimensions.alignment', { defaultValue: 'Alignement objectifs' }),
      score: scoreObjectivesAlignment({ activeObjectifs, projetsActifs }),
      weight: 0.2,
      available: objectifsArr.length > 0 || projectsArr.length > 0,
      detail: t('conseillerTemps.dimensions.alignmentDetail', {
        defaultValue: 'Cohérence entre objectifs définis et projets actifs.',
      }),
      dataSource: t('conseillerTemps.dimensions.alignmentData', {
        defaultValue: 'Données : vos objectifs actifs et vos projets en cours.',
      }),
      emptyHint: t('conseillerTemps.dimensions.alignmentEmpty', {
        defaultValue:
          'Aucun objectif ni projet — définissez-en pour activer ce score.',
      }),
    },
    {
      key: 'balance',
      label: t('conseillerTemps.dimensions.balance', { defaultValue: 'Équilibre de vie' }),
      score: scoreLifeBalance({ wheelOfLife }),
      weight: 0.15,
      available: wheelValues.length > 0,
      detail: t('conseillerTemps.dimensions.balanceDetail', {
        defaultValue: 'Répartition de votre énergie entre les domaines de la roue de vie.',
      }),
      dataSource: t('conseillerTemps.dimensions.balanceData', {
        defaultValue: 'Données : les notes de votre roue de vie.',
      }),
      emptyHint: t('conseillerTemps.dimensions.balanceEmpty', {
        defaultValue: 'Roue de vie non renseignée — complétez-la pour activer ce score.',
      }),
    },
    {
      key: 'sport',
      label: t('conseillerTemps.dimensions.sport', { defaultValue: 'Régularité sport' }),
      score: scoreSportConsistency({ sportSessionsLast30d, sportObjectivePerWeek }),
      weight: 0.1,
      // La dimension est évaluable dès qu'une source sport existe : objectif
      // déclaré, séances Health OU événements « Sport » du calendrier.
      available:
        sportObjectivePerWeek != null ||
        safeArr(sportSessions).length > 0 ||
        calendarSportSessionsLast30d > 0,
      detail: t('conseillerTemps.dimensions.sportDetail', {
        defaultValue: 'Régularité de vos séances par rapport à votre objectif.',
      }),
      dataSource: t('conseillerTemps.dimensions.sportData', {
        defaultValue:
          'Données : vos séances de sport des 30 derniers jours (événements « Sport » du calendrier inclus) et votre objectif hebdomadaire.',
      }),
      emptyHint: t('conseillerTemps.dimensions.sportEmpty', {
        defaultValue:
          'Aucune séance ni objectif sport — planifiez un événement catégorie « Sport » ou renseignez un objectif pour activer ce score.',
      }),
    },
    {
      key: 'roadmap',
      label: t('conseillerTemps.dimensions.roadmap', { defaultValue: 'Vision long terme' }),
      score: scoreRoadmapPlanning({ roadmapIdeasCount: roadmapArr.length, plannedThisQuarter }),
      weight: 0.15,
      available: roadmapArr.length > 0,
      detail: t('conseillerTemps.dimensions.roadmapDetail', {
        defaultValue: 'Idées roadmap planifiées sur l’horizon trimestre / année.',
      }),
      dataSource: t('conseillerTemps.dimensions.roadmapData', {
        defaultValue: 'Données : vos idées de roadmap et celles planifiées ce trimestre.',
      }),
      emptyHint: t('conseillerTemps.dimensions.roadmapEmpty', {
        defaultValue: 'Roadmap vide — ajoutez des idées pour activer ce score.',
      }),
    },
  ].map((d) => ({
    ...d,
    status: d.available ? statusFromScore(d.score) : 'unknown',
  }))

  // LAP-765-v4 — Score global sur les seules dimensions évaluées, avec
  // re-normalisation des poids : si la moitié des dimensions sont vides, le
  // score ne doit pas être divisé par deux artificiellement. Si AUCUNE
  // dimension n'est évaluable, on renvoie `null` (l'UI affiche « — »).
  const evaluatedDims = dimensions.filter((d) => d.available)
  const evaluatedWeight = evaluatedDims.reduce((acc, d) => acc + d.weight, 0)
  const globalScore =
    evaluatedWeight > 0
      ? Math.round(
          evaluatedDims.reduce((acc, d) => acc + d.score * d.weight, 0) /
            evaluatedWeight,
        )
      : null

  // ── Diagnostic (forces / faiblesses / risques) ───────────────────────
  //
  // LAP-765-v4 — Seules les dimensions réellement évaluées (`available`)
  // peuvent devenir une force / faiblesse / risque. Les dimensions vides
  // partent dans `notEvaluated` : l'UI les liste comme « données manquantes »
  // au lieu de les faire passer (à tort) pour des points forts.
  const strengths = dimensions
    .filter((d) => d.available && d.status === 'good')
    .map((d) => ({
      key: d.key,
      title: d.label,
      detail: d.detail,
    }))
  const weaknesses = dimensions
    .filter((d) => d.available && d.status === 'warning')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const risks = dimensions
    .filter((d) => d.available && d.status === 'critical')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const notEvaluated = dimensions
    .filter((d) => !d.available)
    .map((d) => ({ key: d.key, title: d.label, detail: d.emptyHint }))

  // LAP-770 — les événements souvent reportés remontent comme risque explicite
  // dans le diagnostic du Maître du temps : c'est un signal de procrastination
  // qui n'est porté par aucune des 6 dimensions de score.
  if (procrastinatedCount > 0) {
    const top = procrastinatedEvents
      .slice(0, 3)
      .map((e) => `« ${e.titre || 'Sans titre'} » (${e.rescheduleCount}×)`)
      .join(', ')
    risks.push({
      key: 'procrastinated-events',
      title: t('conseillerTemps.diagnostic.procrastinatedTitle', {
        defaultValue:
          procrastinatedCount > 1
            ? `${procrastinatedCount} événements souvent reportés`
            : '1 événement souvent reporté',
        count: procrastinatedCount,
      }),
      detail: t('conseillerTemps.diagnostic.procrastinatedDetail', {
        defaultValue: `Replanifié(s) au moins ${RESCHEDULE_THRESHOLD} fois : ${top}. Tranchez : faites-le, déléguez-le, ou abandonnez-le.`,
        list: top,
        threshold: RESCHEDULE_THRESHOLD,
      }),
    })
  }

  // ── Actions prioritaires ─────────────────────────────────────────────
  const actionsPrioritaires = []

  if (overdueTasks > 0) {
    actionsPrioritaires.push({
      id: 'overdue-tasks',
      title: t('conseillerTemps.actions.overdueTitle', {
        defaultValue: 'Solder vos tâches en retard',
        count: overdueTasks,
      }),
      description: t('conseillerTemps.actions.overdueDesc', {
        defaultValue: `${overdueTasks} tâche(s) en retard à reprogrammer ou abandonner cette semaine.`,
        count: overdueTasks,
      }),
      impact: 'high',
      effortMinutes: 30,
      route: '/todos',
    })
  }
  if (procrastinatedCount > 0) {
    // LAP-770 — action dédiée aux événements qu'on repousse en boucle.
    actionsPrioritaires.push({
      id: 'procrastinated-events',
      title: t('conseillerTemps.actions.procrastinatedTitle', {
        defaultValue: 'Clarifier les événements souvent reportés',
      }),
      description: t('conseillerTemps.actions.procrastinatedDesc', {
        defaultValue: `${procrastinatedCount} événement(s) déplacé(s) ≥ ${RESCHEDULE_THRESHOLD} fois. Pour chacun : décidez de le faire à date fixe, de le déléguer, ou de le supprimer.`,
        count: procrastinatedCount,
        threshold: RESCHEDULE_THRESHOLD,
      }),
      impact: 'high',
      effortMinutes: 15,
      route: '/MonCalendrier',
    })
  }
  if (eventsPerWeek > weeklyCapacity * 1.1) {
    actionsPrioritaires.push({
      id: 'overload',
      title: t('conseillerTemps.actions.overloadTitle', {
        defaultValue: 'Délester la semaine',
      }),
      description: t('conseillerTemps.actions.overloadDesc', {
        defaultValue:
          'Votre semaine semble surchargée. Identifiez deux événements à reporter ou à déléguer.',
      }),
      impact: 'high',
      effortMinutes: 20,
      route: '/MonCalendrier',
    })
  }
  if (activeObjectifs === 0) {
    actionsPrioritaires.push({
      id: 'no-objectives',
      title: t('conseillerTemps.actions.noObjectivesTitle', {
        defaultValue: 'Définir vos objectifs annuels',
      }),
      description: t('conseillerTemps.actions.noObjectivesDesc', {
        defaultValue:
          'Sans objectifs explicites, le temps ne s’aligne sur rien. Posez 1 à 3 objectifs clés.',
      }),
      impact: 'high',
      effortMinutes: 45,
      route: '/mesobjectifs',
    })
  }
  if (sportSessionsLast30d < 4) {
    actionsPrioritaires.push({
      id: 'sport-block',
      title: t('conseillerTemps.actions.sportTitle', {
        defaultValue: 'Bloquer 2 créneaux sport',
      }),
      description: t('conseillerTemps.actions.sportDesc', {
        defaultValue:
          'Posez deux créneaux récurrents de 45 min cette semaine — la régularité fait tout.',
      }),
      impact: 'medium',
      effortMinutes: 10,
      route: '/MonCalendrier',
    })
  }
  if (roadmapArr.length === 0) {
    actionsPrioritaires.push({
      id: 'roadmap-empty',
      title: t('conseillerTemps.actions.roadmapTitle', {
        defaultValue: 'Initialiser votre roadmap',
      }),
      description: t('conseillerTemps.actions.roadmapDesc', {
        defaultValue:
          'Listez 5 idées sur l’année à venir. Sans vision, l’agenda devient un puits sans fond.',
      }),
      impact: 'medium',
      effortMinutes: 25,
      route: '/Roadmap',
    })
  }

  // ── Suggestions de time-blocks par horizon ───────────────────────────
  const suggestedBlocks = {
    week: buildWeekBlocks({ interests, concerns, t }),
    month: buildMonthBlocks({ roadmapArr, objectifsArr, t }),
    quarter: buildQuarterBlocks({ roadmapArr, t }),
    year: buildYearBlocks({ interests, t }),
  }

  // ── Recommandations narratives ───────────────────────────────────────
  const recommendations = []
  // LAP-765-v2 — Un mot sur l'emploi pour ancrer le diagnostic.
  if (employment?.jobTitle) {
    if (workHours >= 40) {
      recommendations.push(
        t('conseillerTemps.reco.heavyJob', {
          defaultValue: `Avec ${workHours}h/semaine déclarées sur "${employment.jobTitle}", protégez vos soirées et un demi-jour de "vrai repos" chaque semaine — sans quoi le reste s’érode.`,
          hours: workHours,
          job: employment.jobTitle,
        }),
      )
    } else if (workHours > 0) {
      recommendations.push(
        t('conseillerTemps.reco.partTimeJob', {
          defaultValue: `Votre poste "${employment.jobTitle}" représente ~${workHours}h/semaine : il vous reste donc une vraie marge personnelle, profitez-en pour avancer un projet long terme.`,
          hours: workHours,
          job: employment.jobTitle,
        }),
      )
    }
  }
  if (interests.length) {
    recommendations.push(
      t('conseillerTemps.reco.interests', {
        defaultValue: `Sur vos centres d’intérêt (${interests.slice(0, 3).join(', ')}), pensez à réserver chaque semaine au moins un créneau actif et un créneau de lecture/passive.`,
        list: interests.slice(0, 3).join(', '),
      }),
    )
  }
  if (concerns.length) {
    recommendations.push(
      t('conseillerTemps.reco.concerns', {
        defaultValue: `Vos préoccupations principales (${concerns.slice(0, 2).join(', ')}) méritent un bloc « réflexion » mensuel : 30 min pour faire le point.`,
        list: concerns.slice(0, 2).join(', '),
      }),
    )
  }

  // ── Suggested prompts pour le chat ───────────────────────────────────
  // LAP-765 — variété : en plus des 4 prompts génériques, on pousse en tête
  // quelques prompts CONTEXTUELS déduits des mêmes signaux déjà calculés
  // ci-dessus (retard, procrastination, sport, préoccupations). Ça évite que
  // le conseiller propose toujours les 4 mêmes puces, sans ajouter de calcul
  // supplémentaire. Plafonné pour rester lisible dans la rangée de chips.
  const contextualPrompts = []
  if (overdueTasks > 0) {
    contextualPrompts.push(
      t('conseillerTemps.prompts.overdueTasks', {
        defaultValue: `J'ai ${overdueTasks} tâche(s) en retard, par où je commence ?`,
        count: overdueTasks,
      }),
    )
  }
  if (procrastinatedCount > 0) {
    contextualPrompts.push(
      t('conseillerTemps.prompts.procrastination', {
        defaultValue: 'Pourquoi je reporte toujours les mêmes événements ?',
      }),
    )
  }
  if (sportObjectivePerWeek && sportSessionsLast30d / 4.33 < sportObjectivePerWeek) {
    contextualPrompts.push(
      t('conseillerTemps.prompts.sportGap', {
        defaultValue: 'Comment rattraper mon objectif sport ce mois-ci ?',
      }),
    )
  }
  if (concerns.length) {
    contextualPrompts.push(
      t('conseillerTemps.prompts.topConcern', {
        defaultValue: `Aide-moi à avancer sur : ${concerns[0]}.`,
        concern: concerns[0],
      }),
    )
  }

  const suggestedPrompts = [
    ...contextualPrompts,
    t('conseillerTemps.prompts.organizeWeek', {
      defaultValue: 'Comment organiser ma semaine prochaine ?',
    }),
    t('conseillerTemps.prompts.quarterPlan', {
      defaultValue: 'Aide-moi à planifier mon prochain trimestre.',
    }),
    t('conseillerTemps.prompts.balance', {
      defaultValue: 'Quels domaines de ma vie je néglige en ce moment ?',
    }),
    t('conseillerTemps.prompts.deprioritize', {
      defaultValue: 'Quelles tâches je peux abandonner cette semaine ?',
    }),
  ].slice(0, 6)

  // LAP-765-v2 — Répartition du temps par catégorie sur 30 jours, à partir
  // des occurrences dépliées (donc inclut les échéanciers).
  const breakdown = computeTimeBreakdown(monthOccurrences)
  // On ajoute aussi une "ligne virtuelle" pour le travail si l'utilisateur a
  // un emploi déclaré, même s'il n'a pas mis ses heures de bureau dans le
  // calendrier — c'est plus honnête pour la répartition affichée.
  if (workHours > 0) {
    const workMinutesOver30d = workHours * 60 * 4.33 // 4.33 semaines / mois
    const totalWithWork = breakdown.totalMinutes + workMinutesOver30d
    const recomputed = [
      {
        category: t('conseillerTemps.breakdown.workLabel', {
          defaultValue: 'Travail',
        }),
        minutes: Math.round(workMinutesOver30d),
        hours: Math.round((workMinutesOver30d / 60) * 10) / 10,
        percent: Math.round((workMinutesOver30d / totalWithWork) * 100),
        color: '#a987ff',
        synthetic: true,
      },
      ...breakdown.entries.map((e) => ({
        ...e,
        percent:
          totalWithWork > 0 ? Math.round((e.minutes / totalWithWork) * 100) : 0,
      })),
    ].sort((a, b) => b.minutes - a.minutes)
    breakdown.entries = recomputed
    breakdown.totalMinutes = totalWithWork
  }

  return {
    userProfile: {
      firstName,
      interests,
      concerns,
    },
    employment, // LAP-765-v2
    metrics: {
      eventsThisWeek: eventsThisWeek.length,
      eventsThisMonth: eventsThisMonth.length,
      weeklyEventHours, // LAP-765-v2 — heures occupées par les récurrences
      pendingTasks,
      overdueTasks,
      projetsActifs,
      activeObjectifs,
      roadmapIdeasCount: roadmapArr.length,
      sportSessionsLast30d,
      // Détail de la provenance : séances Health vs événements « Sport » du
      // calendrier (30 derniers jours, récurrences dépliées).
      healthSportSessionsLast30d,
      calendarSportSessionsLast30d,
      weeklyCapacity,
      workHoursPerWeek: workHours, // LAP-765-v2
      procrastinatedCount, // LAP-770
    },
    // LAP-770 — détail des événements souvent reportés (pour l'UI / le chat).
    procrastinatedEvents,
    timeScore: {
      global: globalScore,
      dimensions,
      // LAP-765-v4 — couverture du diagnostic (X dimensions sur 6 évaluées).
      evaluatedCount: evaluatedDims.length,
      totalCount: dimensions.length,
    },
    diagnostic: {
      strengths,
      weaknesses,
      risks,
      notEvaluated,
    },
    timeBreakdown: breakdown, // LAP-765-v2
    actionsPrioritaires,
    suggestedBlocks,
    recommendations,
    suggestedPrompts,
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Horizon-specific generators (pure helpers, no LLM)
// ─────────────────────────────────────────────────────────────────────────

function buildWeekBlocks({ interests = [], concerns = [], t }) {
  const blocks = [
    {
      id: 'week-deep-work',
      // LAP-765-v3 — catégorie métier, reprise dans `data` à la programmation.
      category: t('conseillerTemps.categories.focus', { defaultValue: 'Concentration' }),
      title: t('conseillerTemps.blocks.weekDeepWork', { defaultValue: 'Bloc « deep work »' }),
      duration: '2h',
      cadence: t('conseillerTemps.blocks.cadence2pw', { defaultValue: '2× / semaine' }),
      detail: t('conseillerTemps.blocks.weekDeepWorkDetail', {
        defaultValue: 'Matinée sans interruption pour avancer sur votre projet principal.',
      }),
    },
    {
      id: 'week-review',
      category: t('conseillerTemps.categories.organisation', { defaultValue: 'Organisation' }),
      title: t('conseillerTemps.blocks.weekReview', { defaultValue: 'Revue de semaine' }),
      duration: '30 min',
      cadence: t('conseillerTemps.blocks.cadence1pw', { defaultValue: '1× / semaine' }),
      detail: t('conseillerTemps.blocks.weekReviewDetail', {
        defaultValue: 'Vendredi PM ou dimanche : bilan, replanification, célébration des wins.',
      }),
    },
    {
      id: 'week-sport',
      category: t('conseillerTemps.categories.sport', { defaultValue: 'Sport' }),
      title: t('conseillerTemps.blocks.weekSport', { defaultValue: 'Activité physique' }),
      duration: '45 min',
      cadence: t('conseillerTemps.blocks.cadence3pw', { defaultValue: '3× / semaine' }),
      detail: t('conseillerTemps.blocks.weekSportDetail', {
        defaultValue: 'Sport, marche, mobilité — la régularité prime sur l’intensité.',
      }),
    },
  ]
  if (interests.length) {
    blocks.push({
      id: 'week-interest',
      category: t('conseillerTemps.categories.leisure', { defaultValue: 'Loisirs' }),
      title: t('conseillerTemps.blocks.weekInterest', {
        defaultValue: `Temps « ${interests[0]} »`,
        topic: interests[0],
      }),
      duration: '1h',
      cadence: t('conseillerTemps.blocks.cadence1pw', { defaultValue: '1× / semaine' }),
      detail: t('conseillerTemps.blocks.weekInterestDetail', {
        defaultValue: 'Un créneau dédié à votre centre d’intérêt prioritaire.',
      }),
    })
  }
  if (concerns.length) {
    blocks.push({
      id: 'week-worry',
      category: t('conseillerTemps.categories.wellbeing', { defaultValue: 'Bien-être' }),
      title: t('conseillerTemps.blocks.weekWorry', {
        defaultValue: 'Bloc « décompression »',
      }),
      duration: '20 min',
      cadence: t('conseillerTemps.blocks.cadence3pw', { defaultValue: '3× / semaine' }),
      detail: t('conseillerTemps.blocks.weekWorryDetail', {
        defaultValue: 'Marche, respiration, méditation — pour absorber le stress courant.',
      }),
    })
  }
  return blocks
}

function buildMonthBlocks({ roadmapArr = [], objectifsArr = [], t }) {
  return [
    {
      id: 'month-strategy',
      category: t('conseillerTemps.categories.pilotage', { defaultValue: 'Pilotage' }),
      title: t('conseillerTemps.blocks.monthStrategy', {
        defaultValue: 'Revue stratégique mensuelle',
      }),
      duration: '1h30',
      cadence: t('conseillerTemps.blocks.cadence1pm', { defaultValue: '1× / mois' }),
      detail: t('conseillerTemps.blocks.monthStrategyDetail', {
        defaultValue:
          'Premier samedi : confronter agenda, objectifs, roadmap. Réorienter si besoin.',
      }),
    },
    {
      id: 'month-roadmap',
      category: t('conseillerTemps.categories.projects', { defaultValue: 'Projets' }),
      title: t('conseillerTemps.blocks.monthRoadmap', {
        defaultValue: 'Avancer 1 idée roadmap',
      }),
      duration: '3h',
      cadence: t('conseillerTemps.blocks.cadence1pm', { defaultValue: '1× / mois' }),
      detail:
        roadmapArr.length > 0
          ? t('conseillerTemps.blocks.monthRoadmapDetailWith', {
              defaultValue: `Vous avez ${roadmapArr.length} idée(s) en réserve. Sortez-en une chaque mois.`,
              count: roadmapArr.length,
            })
          : t('conseillerTemps.blocks.monthRoadmapDetailEmpty', {
              defaultValue: 'Commencez par capturer 5 idées sur votre roadmap, puis pickez-en une.',
            }),
    },
    {
      id: 'month-relations',
      category: t('conseillerTemps.categories.relations', { defaultValue: 'Relations' }),
      title: t('conseillerTemps.blocks.monthRelations', {
        defaultValue: 'Relations clés',
      }),
      duration: '2h',
      cadence: t('conseillerTemps.blocks.cadence1pm', { defaultValue: '1× / mois' }),
      detail: t('conseillerTemps.blocks.monthRelationsDetail', {
        defaultValue: 'Un déjeuner ou un appel avec quelqu’un d’important. Pas par devoir, par envie.',
      }),
    },
  ]
}

function buildQuarterBlocks({ roadmapArr = [], t }) {
  return [
    {
      id: 'quarter-okr',
      category: t('conseillerTemps.categories.pilotage', { defaultValue: 'Pilotage' }),
      title: t('conseillerTemps.blocks.quarterOkr', {
        defaultValue: 'Définir 3 objectifs trimestriels',
      }),
      duration: '2h',
      cadence: t('conseillerTemps.blocks.cadence1pq', { defaultValue: '1× / trimestre' }),
      detail: t('conseillerTemps.blocks.quarterOkrDetail', {
        defaultValue:
          'Maximum 3 objectifs concrets, mesurables, avec une date butoir. Le reste se range derrière.',
      }),
    },
    {
      id: 'quarter-retro',
      category: t('conseillerTemps.categories.pilotage', { defaultValue: 'Pilotage' }),
      title: t('conseillerTemps.blocks.quarterRetro', {
        defaultValue: 'Rétro trimestrielle',
      }),
      duration: '1h',
      cadence: t('conseillerTemps.blocks.cadence1pq', { defaultValue: '1× / trimestre' }),
      detail: t('conseillerTemps.blocks.quarterRetroDetail', {
        defaultValue:
          'Ce qui a marché, ce qui n’a pas, ce qui change. Pose les bases du trimestre suivant.',
      }),
    },
  ]
}

// ─────────────────────────────────────────────────────────────────────────
// LAP-765-v4 — Formatage des dates / heures pour `POST /api/time/events/`.
//
// CORRECTIF : l'endpoint `/api/time/events/` est un `ModelViewSet` DRF
// (EventViewSet + EventSerializer). Ses `DateField` n'acceptent en entrée
// que l'ISO-8601 `AAAA-MM-JJ` — le réglage `DATE_FORMAT` côté REST_FRAMEWORK
// ne pilote QUE l'affichage en sortie, pas le parsing en entrée.
//
// Les versions v2/v3 envoyaient `JJ-MM-AAAA` (en croyant — à tort — viser le
// formulaire Django `EventForm`). DRF rejetait alors la requête avec un
// HTTP 400 « Date has wrong format », capté silencieusement → snackbar
// « réponse inattendue du serveur ». On envoie désormais de l'ISO, comme le
// vrai formulaire calendrier (`EventForm.js`) le fait déjà.
//
// Factorisé ici, réutilisé par les deux générateurs de payload : suggestions
// (timeBlockToEventPayload) + ajout rapide (quickAddToEventPayload).
// ─────────────────────────────────────────────────────────────────────────
function formatDateISO(d) {
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

function formatTimeHHMM(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`
}

// LAP-765-v3 — Prochaine date (≥ aujourd'hui) tombant sur l'un des jours de
// semaine fournis (flags 'monday'…'sunday'). Sert d'ancrage `start_date` à un
// échéancier hebdomadaire saisi via l'ajout rapide.
function nextWeekdayDate(weekDayFlags, hh = 9, mm = 0) {
  const targetJsDow = safeArr(weekDayFlags)
    .map((flag) => DOW_FLAG_BY_INDEX.indexOf(flag))
    .filter((i) => i >= 0)
    .map((i) => (i === 6 ? 0 : i + 1)) // ISO (lundi=0) → JS getDay (dimanche=0)
  const base = new Date()
  base.setHours(hh, mm, 0, 0)
  if (!targetJsDow.length) return base
  for (let add = 0; add < 14; add += 1) {
    const cand = new Date(base.getTime() + add * 86400000)
    if (targetJsDow.includes(cand.getDay())) return cand
  }
  return base
}

// ─────────────────────────────────────────────────────────────────────────
// LAP-765-v2 — Helper de scheduling : transforme un time-block (semaine /
// mois / trimestre / année) en payload pour `POST /api/time/events/`.
//
// Le mapping respecte le modèle Event Django :
//   - is_periodic + is_weekly/is_monthly/is_yearly pour les récurrences,
//   - repeat_after pour les cadences non-1× (ex: "1× / trimestre" = monthly
//     repeat_after=3),
//   - start_date / end_date / start_time / end_time au bon format,
//   - frequency_end_date à 1 an dans le futur par défaut (sinon backend
//     considère parfois l'événement comme infini, ce qui pollue le calendrier).
//
// La fonction renvoie aussi `horizon` pour que la page sache comment annoncer
// l'événement créé dans son snackbar.
// ─────────────────────────────────────────────────────────────────────────
export function timeBlockToEventPayload(block, horizon, refDate = new Date()) {
  const durationMin = parseDurationToMinutes(block.duration)
  const start = pickStartSlot(horizon, refDate)
  const end = new Date(start.getTime() + durationMin * 60000)

  // LAP-765-v4 — formatage factorisé (cf. formatDateISO / formatTimeHHMM).
  const fmt = formatDateISO
  const fmtTime = formatTimeHHMM

  const recurrence = cadenceToRecurrence(block.cadence, horizon)
  const oneYearLater = new Date(start.getTime() + 365 * 24 * 3600 * 1000)

  // LAP-765-v3 — Une cadence "N× / semaine" est désormais étalée sur N jours
  // distincts (cf. cadenceToRecurrence → weekDays). On pose un flag booléen
  // par jour actif : le backend crée alors N occurrences réelles par semaine,
  // et la synthèse temps les compte toutes — et non plus une seule.
  const activeWeekDays = recurrence.weekly
    ? recurrence.weekDays || ['monday']
    : []
  const hasDay = (name) => activeWeekDays.includes(name)

  return {
    event_type: 'a',
    is_all_day: durationMin >= 240, // ≥ 4h → all-day
    titre: block.title,
    lieux: '',
    start_date: fmt(start),
    end_date: fmt(end),
    start_time: fmtTime(start),
    end_time: fmtTime(end),
    is_periodic: !!recurrence.periodic,
    is_daily: !!recurrence.daily,
    is_weekly: !!recurrence.weekly,
    is_monthly: !!recurrence.monthly,
    is_yearly: !!recurrence.yearly,
    repeat_after: recurrence.repeatAfter || 0,
    monday: hasDay('monday'),
    tuesday: hasDay('tuesday'),
    wednesday: hasDay('wednesday'),
    thursday: hasDay('thursday'),
    friday: hasDay('friday'),
    saturday: hasDay('saturday'),
    sunday: hasDay('sunday'),
    // LAP-765-v4 — `null` (et non `''`) pour les blocs non récurrents : un
    // DateField DRF rejette la chaîne vide avec un HTTP 400.
    frequency_end_date: recurrence.periodic ? fmt(oneYearLater) : null,
    is_freq_end_date: !!recurrence.periodic,
    data: JSON.stringify({
      source: 'time-advisor',
      blockId: block.id,
      horizon,
      detail: block.detail,
      // LAP-765-v3 — Catégorie métier du bloc : relue par categoryOf() pour
      // que l'occurrence alimente la bonne ligne de la répartition du temps.
      category: block.category || null,
      // Nombre d'occurrences/semaine visé (trace debug + futur backend).
      weeklyCount: recurrence.count || 1,
    }),
  }
}

// ─────────────────────────────────────────────────────────────────────────
// LAP-765-v3 — Ajout rapide : transforme la saisie libre du mini-formulaire
// (titre / catégorie / récurrence / jours / date / heure / durée) en payload
// `POST /api/time/events/`. Même contrat backend que timeBlockToEventPayload,
// mais la date d'ancrage et la cadence viennent de l'utilisateur et non d'un
// horizon prédéfini.
//
//   recurrence : 'once' | 'weekly' | 'monthly' | 'daily'
//   weekDays   : ['monday', 'wednesday', …]  (uniquement si 'weekly')
//   date       : 'YYYY-MM-DD'  (récurrences 'once' et 'monthly')
//
// L'événement créé porte `data.category` → relu par categoryOf() pour la
// répartition du temps, exactement comme les blocs suggérés.
// ─────────────────────────────────────────────────────────────────────────
export function quickAddToEventPayload({
  title = '',
  category = null,
  recurrence = 'weekly',
  weekDays = ['monday'],
  date = null,
  startTime = '09:00',
  durationMinutes = 60,
} = {}) {
  const [rawHH, rawMM] = String(startTime)
    .split(':')
    .map((n) => parseInt(n, 10))
  const hh = Number.isFinite(rawHH) ? rawHH : 9
  const mm = Number.isFinite(rawMM) ? rawMM : 0

  const isWeekly = recurrence === 'weekly'
  const isMonthly = recurrence === 'monthly'
  const isDaily = recurrence === 'daily'
  const periodic = recurrence !== 'once'

  const activeDays =
    isWeekly && Array.isArray(weekDays) && weekDays.length ? weekDays : []

  // Date d'ancrage du premier créneau.
  let start
  if (isWeekly) {
    start = nextWeekdayDate(activeDays.length ? activeDays : ['monday'], hh, mm)
  } else if (date) {
    start = new Date(`${date}T00:00:00`)
    start.setHours(hh, mm, 0, 0)
  } else {
    start = new Date()
    start.setHours(hh, mm, 0, 0)
  }
  if (Number.isNaN(start.getTime())) {
    start = new Date()
    start.setHours(hh, mm, 0, 0)
  }

  const dur = Math.max(5, num(durationMinutes, 60))
  const end = new Date(start.getTime() + dur * 60000)
  const oneYearLater = new Date(start.getTime() + 365 * 24 * 3600 * 1000)
  const hasDay = (name) => activeDays.includes(name)

  return {
    event_type: 'a',
    is_all_day: dur >= 240,
    titre: title,
    lieux: '',
    start_date: formatDateISO(start),
    end_date: formatDateISO(end),
    start_time: formatTimeHHMM(start),
    end_time: formatTimeHHMM(end),
    is_periodic: periodic,
    is_daily: isDaily,
    is_weekly: isWeekly,
    is_monthly: isMonthly,
    is_yearly: false,
    repeat_after: periodic ? 1 : 0,
    monday: hasDay('monday'),
    tuesday: hasDay('tuesday'),
    wednesday: hasDay('wednesday'),
    thursday: hasDay('thursday'),
    friday: hasDay('friday'),
    saturday: hasDay('saturday'),
    sunday: hasDay('sunday'),
    // Le backend déplie le mensuel via `repeat_on` (jour du mois).
    repeat_on: isMonthly ? start.getDate() : null,
    // LAP-765-v4 — `null` (et non `''`) pour le ponctuel : DRF rejette la
    // chaîne vide sur un DateField.
    frequency_end_date: periodic ? formatDateISO(oneYearLater) : null,
    is_freq_end_date: periodic,
    data: JSON.stringify({
      source: 'time-advisor-quickadd',
      detail: '',
      category: category || null,
    }),
  }
}

function parseDurationToMinutes(duration = '') {
  if (!duration) return 60
  const d = String(duration).toLowerCase().replace(/\s+/g, '')
  // "2h", "2h30", "1h"
  let m = d.match(/^(\d+)h(\d{0,2})$/)
  if (m) return parseInt(m[1], 10) * 60 + (parseInt(m[2], 10) || 0)
  // "30min", "45min"
  m = d.match(/^(\d+)min$/)
  if (m) return parseInt(m[1], 10)
  // "1h30min"
  m = d.match(/^(\d+)h(\d+)min$/)
  if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10)
  // "½journée"
  if (d.includes('journée') || d.includes('demi')) return 4 * 60
  // "2semaines" / "40hcumulées" → un slot de 1h initial (le reste se reprogramme)
  return 60
}

function pickStartSlot(horizon, ref) {
  const d = new Date(ref.getTime())
  d.setSeconds(0, 0)
  switch (horizon) {
    case 'week': {
      // Prochain lundi 09:00.
      const day = d.getDay() || 7
      const delta = day === 1 ? 7 : 8 - day
      d.setDate(d.getDate() + delta)
      d.setHours(9, 0, 0, 0)
      return d
    }
    case 'month': {
      // Premier samedi du mois prochain, 10:00.
      d.setMonth(d.getMonth() + 1, 1)
      while (d.getDay() !== 6) d.setDate(d.getDate() + 1)
      d.setHours(10, 0, 0, 0)
      return d
    }
    case 'quarter': {
      // Dans 1 mois, mercredi 18:00.
      d.setMonth(d.getMonth() + 1)
      while (d.getDay() !== 3) d.setDate(d.getDate() + 1)
      d.setHours(18, 0, 0, 0)
      return d
    }
    case 'year':
    default: {
      // Dans 30 jours, samedi 10:00.
      d.setDate(d.getDate() + 30)
      while (d.getDay() !== 6) d.setDate(d.getDate() + 1)
      d.setHours(10, 0, 0, 0)
      return d
    }
  }
}

// LAP-765-v3 — Étalement d'une cadence hebdo multi-occurrences sur des jours
// distincts. "3× / semaine" → lundi / mercredi / vendredi : chaque séance
// devient un vrai créneau, créé puis compté individuellement dans la synthèse.
const WEEKLY_SPREAD_BY_COUNT = {
  1: ['monday'],
  2: ['monday', 'thursday'],
  3: ['monday', 'wednesday', 'friday'],
  4: ['monday', 'tuesday', 'thursday', 'friday'],
  5: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  6: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
  7: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
}

// LAP-765-v3 — Lit le multiplicateur d'une cadence ("3× / semaine" → 3). Le
// libellé est traduit (i18next) mais le chiffre reste exploitable quelle que
// soit la langue. Borné à [1..7] (7 jours/semaine maximum).
function parseCadenceCount(cadenceLabel = '') {
  const m = String(cadenceLabel).match(/(\d+)/)
  const n = m ? parseInt(m[1], 10) : 1
  return Number.isFinite(n) && n > 0 ? Math.min(n, 7) : 1
}

function cadenceToRecurrence(cadenceLabel = '', horizon) {
  const c = String(cadenceLabel).toLowerCase()
  // LAP-765-v3 — Jours hebdo déduits du multiplicateur "N× / semaine".
  const weekDays = WEEKLY_SPREAD_BY_COUNT[parseCadenceCount(cadenceLabel)]
  const count = weekDays.length
  if (c.includes('semaine') && !c.includes('mois') && !c.includes('an')) {
    return { periodic: true, weekly: true, repeatAfter: 1, weekDays, count }
  }
  if (c.includes('mois')) {
    return { periodic: true, monthly: true, repeatAfter: 1 }
  }
  if (c.includes('trimestre')) {
    return { periodic: true, monthly: true, repeatAfter: 3 }
  }
  if (c.includes('an')) {
    return { periodic: true, yearly: true, repeatAfter: 1 }
  }
  // Fallback : on prend l'horizon comme indice.
  if (horizon === 'week')
    return { periodic: true, weekly: true, repeatAfter: 1, weekDays, count }
  if (horizon === 'month') return { periodic: true, monthly: true, repeatAfter: 1 }
  if (horizon === 'quarter')
    return { periodic: true, monthly: true, repeatAfter: 3 }
  if (horizon === 'year') return { periodic: true, yearly: true, repeatAfter: 1 }
  return { periodic: false }
}

function buildYearBlocks({ interests = [], t }) {
  return [
    {
      id: 'year-vision',
      category: t('conseillerTemps.categories.pilotage', { defaultValue: 'Pilotage' }),
      title: t('conseillerTemps.blocks.yearVision', {
        defaultValue: 'Vision annuelle',
      }),
      duration: '½ journée',
      cadence: t('conseillerTemps.blocks.cadence1py', { defaultValue: '1× / an' }),
      detail: t('conseillerTemps.blocks.yearVisionDetail', {
        defaultValue:
          'Janvier ou anniversaire : 3 thèmes pour l’année, 1 chose dont vous voulez vous souvenir.',
      }),
    },
    {
      id: 'year-deep-rest',
      category: t('conseillerTemps.categories.rest', { defaultValue: 'Repos' }),
      title: t('conseillerTemps.blocks.yearRest', {
        defaultValue: 'Vraies vacances déconnectées',
      }),
      duration: '2 semaines',
      cadence: t('conseillerTemps.blocks.cadence1py', { defaultValue: '1× / an' }),
      detail: t('conseillerTemps.blocks.yearRestDetail', {
        defaultValue: 'Bloquées dans l’agenda dès janvier, sinon elles ne se posent jamais.',
      }),
    },
    {
      id: 'year-learn',
      category: t('conseillerTemps.categories.learning', { defaultValue: 'Apprentissage' }),
      title: t('conseillerTemps.blocks.yearLearn', {
        defaultValue: `Apprentissage long (${interests[0] || 'sujet phare'})`,
        topic: interests[0] || '',
      }),
      duration: '40h cumulées',
      cadence: t('conseillerTemps.blocks.cadence1py', { defaultValue: '1× / an' }),
      detail: t('conseillerTemps.blocks.yearLearnDetail', {
        defaultValue:
          'Une compétence ou un thème suivi sur 12 mois, à raison de ~45 min / semaine.',
      }),
    },
  ]
}
